import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Tooltip } from "antd";
import { useAppSelector } from "../../../..";
import { ServiceRate } from "../../../../lib/types";
import { CommonChildTableProps, DictionaryDto, TableColumn } from "../../../../lib/interfaces";

import {
  checkIsLoading,
  geDicValueById,
  getCommonChildTableProps,
  getDictionaryItemById,
  round,
  toDisplayFormat,
  toHttpFormat,
} from "../../../../lib/utils";
import SpanNumberFormat from "../../SpanNumberFormat";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { ReactComponent as TransitionIcon } from "../../../../img/transition_icon.svg";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import { fakeBalanceType } from "../utils/fakeBalanceType";
import { ReactComponent as CopyIcon } from "../../../../img/copy-icon.svg";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import "./ServiceRateView.less";

type TableDataRecord = ServiceRate<string> &
  CommonChildTableProps & {
    period: string;
  };

type FieldName = keyof TableDataRecord | "btns" | "actions";

type ColumnWidths = Partial<Record<FieldName, string>>;

const columnWidths: ColumnWidths = {
  name: "15%",
  rateModeId: "9%",
  aggregationRuleId: "9%",
  balanceTypeId: "9%",
  rateTypeId: "9%",
  price: "10%",
  priceWithVat: "10%",
  currencyId: "9%",
  priceUnitId: "9%",
  chargeNameId: "9%",
  rangeFrom: "9%",
  rangeTo: "9%",
  period: "",
  actions: "62px",
  btns: "48px",
};

interface ComponentProps {
  serviceRates: ServiceRate<string>[];
  aggregationRules?: AggregationRule<"view">[];
  showHistoryModal: (rateId: number) => void;
  onClickVersionIcon: (rateId: number, isVersionsOpen: boolean) => void;
  isLoading?: boolean;
  isProductDetails?: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ServiceRateView = (props: ComponentProps) => {
  const {
    serviceRates,
    isLoading,
    showHistoryModal,
    onClickVersionIcon,
    isProductDetails,
    aggregationRules,
  } = props;
  const {
    rateType,
    rateTypeLoading,
    rateModesEvents,
    rateModesEventsLoading,
    dicCurrencies,
    dicCurrenciesLoading,
    periodTypesLoading,
    priceUnits,
    priceUnitsLoading,
    chargeNames,
    chargeNamesLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const history = useHistory();
  const { search } = useLocation();
  const offerOnDate = new URLSearchParams(search).get("onDate")?.replace(" ", "+");

  const { tax } = useAppSelector((state) => state.taxTs);
  const { serviceRateVersions, serviceRateVersionsLoading } = useAppSelector(
    (state) => state.offering
  );

  const rowClassName = (record: TableDataRecord) => {
    if (record.isCurrentVersion) {
      return `row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return "row_child row_past-version";
    }
    return "row_child";
  };

  const getColumns = useCallback(
    (): TableColumn<TableDataRecord>[] => [
      {
        title: "Наименование",
        key: "name",
        dataIndex: "name",
        width: columnWidths.name,
      },
      {
        title: "Тип баланса",
        key: "balanceTypeId",
        dataIndex: "balanceTypeId",
        width: columnWidths.balanceTypeId,
        render: (balanceTypeId) => getDictionaryItemById(fakeBalanceType, balanceTypeId)?.name,
      },
      {
        title: "Вид",
        key: "rateModeId",
        dataIndex: "rateModeId",
        width: columnWidths.rateModeId,
        render: (rateModeId) =>
          getDictionaryItemById(rateModesEvents as DictionaryDto[], rateModeId)?.name,
      },
      {
        title: "Группа трафика",
        key: "aggregationRuleId",
        dataIndex: "aggregationRuleId",
        width: columnWidths.aggregationRuleId,
        render: (aggregationRuleId) => {
          if (aggregationRules) {
            return geDicValueById(aggregationRules, aggregationRuleId)?.aggName;
          }
        },
      },
      {
        title: "Тип расчета",
        key: "rateTypeId",
        dataIndex: "rateTypeId",
        width: columnWidths.rateTypeId,
        render: (rateTypeId) =>
          getDictionaryItemById(rateType as DictionaryDto[], rateTypeId)?.name,
      },
      {
        title: "Цена с налогом",
        key: "priceWithVat",
        dataIndex: "priceWithVat",
        width: columnWidths.priceWithVat,
        render: (v, { price, taxRate }) =>
          SpanNumberFormat({ amount: Number(price) * (1 + Number(taxRate) / 100) }),
      },
      {
        title: "Цена без налога",
        key: "price",
        dataIndex: "price",
        width: columnWidths.price,
        render: (price) => <>{SpanNumberFormat({ amount: price })}</>,
      },
      {
        title: "Валюта",
        key: "currencyId",
        dataIndex: "currencyId",
        width: columnWidths.currencyId,
        render: (currencyId) => getDictionaryItemById(dicCurrencies, currencyId)?.name,
      },
      {
        title: "Единица тарификации",
        key: "priceUnitId",
        dataIndex: "priceUnitId",
        width: columnWidths.priceUnitId,
        render: (priceUnitId) =>
          getDictionaryItemById(priceUnits as DictionaryDto[], priceUnitId)?.name,
      },
      {
        title: "Номенклатура",
        key: "chargeNameId",
        dataIndex: "chargeNameId",
        width: columnWidths.chargeNameId,
        render: (chargeNameId) => getDictionaryItemById(chargeNames, chargeNameId)?.name,
      },
      {
        title: "От (шт)",
        key: "rangeFrom",
        dataIndex: "rangeFrom",
        width: columnWidths.rangeFrom,
      },
      {
        title: "До (шт)",
        key: "rangeTo",
        dataIndex: "rangeTo",
        width: columnWidths.rangeTo,
      },
      {
        title: "Период",
        key: "period",
        dataIndex: "period",
        width: columnWidths.period,
      },
      {
        key: "actions",
        width: columnWidths.actions,
        render: (x, { id, offeringId, fromDate }) => {
          const onDatePrice = fromDate ? toHttpFormat(fromDate) : offerOnDate;
          return (
            <>
              <div>
                {!isProductDetails && (
                  <Tooltip title="Скопировать цену">
                    <CopyIcon
                      className="serviceRate-view__copyButton"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (offeringId && id && offerOnDate && onDatePrice) {
                          const path = `/offer/${offeringId}/price/create/${id}?onDate=${offerOnDate}&onDatePrice=${onDatePrice}`;
                          history.push(path);
                        }
                      }}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Перейти к цене">
                  <TransitionIcon
                    className="serviceRate-view__priceBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (offeringId && id && offerOnDate && onDatePrice) {
                        const path = `/offer/${offeringId}/price/${id}?onDate=${offerOnDate}&onDatePrice=${onDatePrice}`;
                        if (isProductDetails) {
                          history.push(`${path}&isProductDetails=true`, { isProductDetails });
                        } else {
                          history.push(path);
                        }
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </>
          );
        },
      },
      {
        key: "btns",
        width: columnWidths.btns,
        render: (x, { id }) => {
          return (
            <>
              <DotsIcon
                className="serviceRate-view__history"
                onClick={(e) => {
                  e.stopPropagation();
                  id && showHistoryModal(id);
                }}
              />
            </>
          );
        },
      },
    ],
    [
      aggregationRules,
      chargeNames,
      dicCurrencies,
      history,
      isProductDetails,
      offerOnDate,
      priceUnits,
      rateModesEvents,
      rateType,
      showHistoryModal,
      tax,
    ]
  );

  const [columns, setColumns] = useState(getColumns());

  // Актуализация отображения колонок
  useEffect(() => {
    const newColumns = getColumns();
    setColumns((prev) => {
      return prev.map((prevColumn) => {
        const newColumn = newColumns.find(({ key }) => key === prevColumn.key);
        return {
          ...prevColumn,
          title: newColumn?.title ? newColumn.title : prevColumn?.title,
          render: newColumn?.render ? newColumn.render : prevColumn?.render,
        };
      });
    });
  }, [getColumns]);

  const tableData = useMemo((): TableDataRecord[] => {
    return [...serviceRates]
      .sort((a, b) => {
        if (a?.id && b?.id) {
          return a.id > b.id ? 1 : -1;
        }
        return 0;
      })
      .map((rate, i) => ({
        ...rate,
        balanceTypeId: rate.balanceTypeId ? rate.balanceTypeId : fakeBalanceType[0].id,
        ...getCommonChildTableProps(rate, i, serviceRates.length),
        period: `${rate.fromDate ? toDisplayFormat(rate.fromDate) : ""} - ${
          rate.toDate ? toDisplayFormat(rate.toDate) : ""
        }`,
      }));
  }, [serviceRates]);

  const childTableData = useMemo((): TableDataRecord[] => {
    return serviceRateVersions.map((rate, i) => ({
      ...rate,
      balanceTypeId: rate.balanceTypeId ? rate.balanceTypeId : fakeBalanceType[0].id,
      ...getCommonChildTableProps(rate, i, serviceRateVersions.length),
      period: `${rate.fromDate ? toDisplayFormat(rate.fromDate) : ""} - ${
        rate.toDate ? toDisplayFormat(rate.toDate) : ""
      }`,
    }));
  }, [serviceRateVersions]);

  const isDictionariesLoading = useMemo(
    () =>
      checkIsLoading(
        rateTypeLoading,
        chargeNamesLoading,
        rateModesEventsLoading,
        dicCurrenciesLoading,
        periodTypesLoading,
        priceUnitsLoading,
        periodTypesLoading
      ),
    [
      rateTypeLoading,
      chargeNamesLoading,
      rateModesEventsLoading,
      dicCurrenciesLoading,
      periodTypesLoading,
      priceUnitsLoading,
      periodTypesLoading,
    ]
  );

  return (
    <div className="serviceRate-view">
      <ToggleableVersionsTable
        columns={columns.filter(({ key }) => key !== "actions")}
        childTableColumns={columns}
        setColumns={setColumns}
        tableData={tableData}
        isTableLoading={isDictionariesLoading || isLoading}
        childRowClassName={rowClassName}
        isChildTableLoading={checkIsLoading(serviceRateVersionsLoading)}
        childTableData={childTableData}
        onOpenVersionsCallback={({ id }, isVersionsOpen) => onClickVersionIcon(id, isVersionsOpen)}
        onCloseVersionsCallback={({ id }, isVersionsOpen) => onClickVersionIcon(id, isVersionsOpen)}
        tableName="serviceRate-view"
      />
    </div>
  );
};
